import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Pencil } from "lucide-react";

// Inline balance editor for a Funding Source (bank account, cash, pay card, etc.).
// Click the balance → number input → Enter or ✓ to save, Escape or ✗ to cancel.
// Backend contract: PATCH /api/funding-sources/{id} { current_balance: <n> }
// The backend auto-stamps balance_updated_at so the RefreshBalancesCard staleness
// pill and Money-on-Hand section update in sync.
export default function AccountBalanceCell({ source, size = "md", onUpdated, testIdPrefix = "account-balance" }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const initialBal = source.current_source_balance_optional ?? source.current_balance ?? 0;
  const [value, setValue] = useState(String(initialBal));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = Number(value);
    if (!Number.isFinite(n)) { toast.error("Enter a valid amount"); return; }
    if (n === Number(initialBal)) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/funding-sources/${source.id}`, { current_balance: n });
      onUpdated && onUpdated(data);
      // Milestone celebration — first time the user's balance-edit streak
      // hits 4 or 12 weeks, backend returns a `milestone_unlocked` marker.
      // Fires as its own toast BEFORE the standard "Real Balance updated"
      // one so the celebration doesn't get lost. Backend marks the
      // milestone as celebrated so it won't fire again next week.
      if (data?.milestone_unlocked) {
        const w = data.milestone_unlocked.weeks;
        const emoji = w >= 12 ? "🔥" : "🎉";
        toast.success(`${emoji} ${w}-week balance streak!`, {
          description: w >= 12
            ? "Three straight months of keeping your Real Balance fresh. You're basically a pro at this now."
            : "A full month of weekly balance updates. Your forecast trust is compounding.",
          duration: 8000,
        });
      }
      // Actionable "Real Balance updated!" toast — nudges the user to also
      // review their bills so the forecast stays trustworthy end-to-end.
      // Sonner auto-dismisses after ~10s here; user can tap X or the action
      // to close early. Skips the follow-up nudge when the user is already
      // on /app/bills (they're literally staring at bills — no CTA needed).
      const onBillsPage = window.location.pathname.startsWith("/app/bills");
      toast.success("Real Balance updated!", {
        description: onBillsPage
          ? `${source.name} → ${formatMoney(n)}`
          : `${source.name} → ${formatMoney(n)}. Would you like to review and update your bills?`,
        duration: 10000,
        action: onBillsPage ? undefined : {
          label: "Update my bills →",
          onClick: () => navigate("/app/bills"),
        },
      });
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cancel = () => {
    setEditing(false);
    setValue(String(initialBal));
  };

  const inputCls = size === "lg" ? "w-32 text-lg py-1" : "w-28 text-sm py-0.5";
  const displayCls = size === "lg" ? "text-lg font-semibold" : "text-sm font-semibold";

  if (editing) {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap" data-testid={`${testIdPrefix}-editing`}>
        <input
          type="number" step="0.01"
          className={`${inputCls} px-2 text-right rounded-md border border-[#4A6148] focus:outline-none text-[#2C302E]`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") cancel();
          }}
          autoFocus
          data-testid={`${testIdPrefix}-input`}
        />
        <button type="button" className="btn-primary !px-1.5 !py-0.5 text-xs" onClick={save} disabled={saving} data-testid={`${testIdPrefix}-save`}>
          <Check size={12} />
        </button>
        <button type="button" className="btn-ghost !px-1.5 !py-0.5 text-xs" onClick={cancel} data-testid={`${testIdPrefix}-cancel`}>
          <X size={12} />
        </button>
      </div>
    );
  }

  // At rest the balance renders as a dashed-outline pill with a pencil icon
  // so it reads unambiguously as "click me to edit". Removes the guesswork
  // of "which number am I supposed to update?" the first time a user lands
  // on Step 1. Hover deepens the outline for extra reinforcement.
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to update from your bank app"
      className={`${displayCls} inline-flex items-center gap-1.5 whitespace-nowrap text-[#2C302E] px-2.5 py-1 rounded-lg border border-dashed border-[#B8C4B2] bg-white/60 hover:border-[#4A6148] hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#4A6148]/30`}
      data-testid={`${testIdPrefix}-display`}
    >
      <Pencil size={12} className="text-[#8A918D]" aria-hidden="true" />
      {formatMoney(initialBal)}
    </button>
  );
}
